import { VStack } from '@genial/components';
import { instanceOfErrorListResponse, ErrorListResponse } from '@genial/services';
import { useNavigation } from '@react-navigation/native';
import React, { useState } from 'react';

import { useToastCreator } from '../../../../../hooks/useToastCreator';
import { SignedInNavigationScreenNavigationProp } from '../../../../../routes/types';
import { useSubscriptionActions } from '../../../../../store/subscription/actions';
import {
  SubscriptionCustomerItemProps,
  SubscriptionItemProps,
} from '../../../../../store/subscription/types';
import { defaultErrorMessageSubscription } from '../../../constants';
import { SubscriptionSolicitationAction } from '../SubscriptionSolicitationAction';
import { SubscriptionSolicitationCard } from '../SubscriptionSolicitationCard';
import { SubscriptionSolicitationHeader } from '../SubscriptionSolicitationHeader';
import { SubscriptionSolicitationInProgress } from '../SubscriptionSolicitationInProgress';
import { SubscriptionSolicitationSuccessActions } from '../SubscriptionSolicitationSuccessActions';
import {
  SubscriptionSolicitationSuccessDetails,
  SubscriptionSolicitationSuccessDetailsProps,
} from '../SubscriptionSolicitationSuccessDetails';

import { getSuccessInfoItems } from './constants';

export interface SubscriptionSolicitationContentProps {
  customer: SubscriptionCustomerItemProps;
  subscription: SubscriptionItemProps;
  quantity: number;
  goBack: () => void;
}

export const SubscriptionSolicitationContent: React.FC<SubscriptionSolicitationContentProps> = ({
  quantity,
  customer,
  subscription,
  goBack,
}) => {
  const { executeSubscriptionSolicitation } = useSubscriptionActions();
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { showToast, showToastFromError } = useToastCreator('subscriptionSolicitation');
  const [hasLeftOvers, setHasLeftOvers] = useState(false);
  const [sendingOrder, setSendingOrder] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successInfos, setSuccessInfos] = useState<
    SubscriptionSolicitationSuccessDetailsProps['infoItems']
  >([]);

  const handleFollow = () => {
    navigate('SubscriptionDetails', { subscription, initialTab: 'subscriptionHistory' });
  };

  const handleExecuteSolicitation = () => {
    setSendingOrder(true);
    executeSubscriptionSolicitation(
      subscription.eventId,
      hasLeftOvers,
      customer.pnpAccountId,
      quantity
    )
      .then(() => {
        setSuccess(true);
        setSuccessInfos(getSuccessInfoItems(customer, subscription, quantity));
      })
      .catch((error) => {
        if (instanceOfErrorListResponse(error as object)) {
          (error as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }

        showToast(...defaultErrorMessageSubscription);
      })
      .finally(() => setSendingOrder(false));
  };

  if (success) {
    return (
      <VStack p="inset.sm" space="stack.xxxs">
        <SubscriptionSolicitationHeader onClose={goBack} title="Solicitação de Subscrição" />
        <SubscriptionSolicitationSuccessDetails infoItems={successInfos} />
        <SubscriptionSolicitationSuccessActions onClose={goBack} onFollow={handleFollow} />
      </VStack>
    );
  }

  if (sendingOrder) {
    return (
      <VStack p="inset.sm" space="stack.xxxs">
        <SubscriptionSolicitationHeader onClose={() => {}} title="Solicitação de Subscrição" />
        <SubscriptionSolicitationInProgress />
      </VStack>
    );
  }

  return (
    <VStack p="inset.sm" space="stack.xxxs">
      <SubscriptionSolicitationHeader onClose={goBack} title="Solicitação de Subscrição" />
      <SubscriptionSolicitationCard
        title={subscription.productDisplay}
        customer={customer}
        quantity={quantity}
        subscription={subscription}
        hasLeftOvers={hasLeftOvers}
        setHasLeftOvers={setHasLeftOvers}
      />
      <SubscriptionSolicitationAction title="Solicitar" onPress={handleExecuteSolicitation} />
    </VStack>
  );
};
